import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import {
  Box,
  Button,
  CircularProgress,
} from '@mui/material';

import useChatFunctions from 'app/features/chat/hooks/useChatFunctions';

import {
  useGetContactsQuery,
  useLazyGetContactsQuery,
} from 'app/features/chat/chatApi';

import ContactListItem from './ContactListItem';
import { useLazyGetChatUsersQuery } from 'app/api/mainApi';

import {
  allContactsSelector,
  appendContacts,
  appendChatUsers,
  currentContactSelector,
} from 'app/features/chat/nfChatSlice';

const ContactListContainer = () => {
  const { slug } = useParams();
  const { setCurrentContactBySlugAndSubscribe } = useChatFunctions();
  const currentContact = useSelector(currentContactSelector);
  const allContactsCollection = useSelector(allContactsSelector);
  const dispatch = useDispatch();

  const {
    data: contactsData,
    isFetching,
  } = useGetContactsQuery({ lastActivityAt: null });

  const [
    lazyLoadContacts, { data: lazyLoadedContactsData },
  ] = useLazyGetContactsQuery();

  const [
    lazyLoadChatUsers, { data: lazyLoadedChatUsersData },
  ] = useLazyGetChatUsersQuery();

  const loadMoreContacts = () => {
    const lastActivityAt =
      contactsData.contacts[contactsData.contacts.length - 1].last_activity_at;
    lazyLoadContacts({ lastActivityAt });
  };

  const doChatUsersLazyLoad = (contacts) => {
    const userIds = contacts.map(contact => contact.other_user.id);
    lazyLoadChatUsers({ ids: userIds });
  };

  useEffect(() => {
    if (contactsData) {
      // add to our collection of contacts which drives the UI
      dispatch(appendContacts({ contacts: contactsData.contacts }));

      // and fetch the corresponding chat users
      doChatUsersLazyLoad(contactsData.contacts);
    }
  }, [contactsData]);

  useEffect(() => {
    if (lazyLoadedContactsData) {
      // add to our collection of contacts which drives the UI
      dispatch(appendContacts({ contacts: lazyLoadedContactsData.contacts }));
      // and fetch the corresponding chat users
      doChatUsersLazyLoad(lazyLoadedContactsData.contacts);
    }
  }, [lazyLoadedContactsData]);

  // the following effect is used to set the current contact
  // when the user navigates to the chat page.
  // it is dependent on the allContactsCollection, which is
  // driven by both useGetContactsQuery and useLazyGetContactsQuery.
  // we might have a slug from the url and can use it to set the current contact.
  // if not, we can just take the first contact from our list
  // and set it as currentContact.
  useEffect(() => {
    if (allContactsCollection?.length === 0) return;
    if (currentContact) return;

    setCurrentContactBySlugAndSubscribe(
      slug || allContactsCollection[0]?.other_user.slug,
    );
  }, [allContactsCollection]);

  useEffect(() => {
    if (lazyLoadedChatUsersData) {
      dispatch(appendChatUsers({ chatUsers: lazyLoadedChatUsersData }));
    }
  }, [lazyLoadedChatUsersData]);

  return (
    <Box>
      {isFetching && (
        <Box>
          <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />
        </Box>
      )}

      {allContactsCollection?.map((contact) => {
        return (
          <ContactListItem
            key={contact.id}
            contact={contact}
          />
        );
      })}
      {!contactsData?.all_contacts_loaded && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button onClick={loadMoreContacts}>
            Load more
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ContactListContainer;
